import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Button, Stack } from '@mui/material';
import type { ExerciseSessionControls } from 'Phaser/Scenes/OSMDExerciseScene';
import ExercisePlayShell from './ExercisePlayShell';
import { resolvePlaySession } from './resolvePlaySession';

/**
 * The real, user-facing play screen for the Exercises section (MUS-481). Reached
 * by tapping a level's tier on the overview (`/exercises`), NOT by a debug URL. It
 * resolves the tapped level/tier into a sitting and mounts the shared play shell
 * (scene + host MIDI bridge + return-nav) with real Start / Submit / Next / End
 * controls.
 *
 * Real multi-take loop: Start opens the first take, Submit grades the played notes
 * (the scene renders the gradient), Next advances to the next take, and repeating
 * Submit -> Next is what makes the cumulative-clear and mastery-streak bars
 * reachable - so the earned point writes and the star lights on the overview. End
 * finishes the sitting, and the return-nav sends the user back to `/exercises`.
 *
 * Deliberately minimal chrome: no write-state / frontier debug readouts and no
 * per-take progression HUD (the take counter / "2 clean, 1 to go" presentation is
 * MUS-482). This is the thin real spine that closes the loop.
 *
 * Next is enabled only in the post-submit window - the machine admits NEXT from its
 * passed / failed / no-input states (never mid-take), so the control tracks that
 * window rather than sitting always-live like the dev launcher's debug button. The
 * window is derived from the control presses themselves (the scene's emitted-event
 * surface does not expose the raw machine phase), which covers the graded and the
 * no-input takes uniformly and never strands the user.
 *
 * Registered flag-gated on USE_EXERCISES in App.tsx (never in production, where the
 * flag is force-off); the shell is dark behind the same flag as defense in depth.
 */

/** Where the take loop is, as far as the controls are concerned. */
type TakePhase =
  | 'idle' // before the first Start - only Start is live
  | 'inTake' // a take is open (playing / awaiting input) - Submit is live
  | 'canAdvance' // a take was submitted (machine in passed/failed/noInput) - Next is live
  | 'done'; // the sitting was ended - the return-nav takes over

const ExercisePlay: React.FC = () => {
  const params = useParams<{ levelNumber?: string; tierNumber?: string }>();
  const levelNumber = Number(params.levelNumber);
  const tierParsed = Number(params.tierNumber);
  const tierNumber = Number.isFinite(tierParsed) ? tierParsed : undefined;

  const session = React.useMemo(
    () => (Number.isFinite(levelNumber) ? resolvePlaySession(levelNumber, tierNumber) : null),
    [levelNumber, tierNumber],
  );

  const [phase, setPhase] = React.useState<TakePhase>('idle');

  // Unknown level/tier (a hand-typed or stale URL): fall back to the overview
  // rather than mounting a scene with no content.
  if (!session) return <Navigate to="/exercises" replace />;

  return (
    <ExercisePlayShell session={session} containerProps={{ 'data-exercise-play': true }}>
      {(controls: ExerciseSessionControls | null) => {
        const ready = !!controls;
        return (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              position: 'fixed',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 20,
            }}
          >
            <Button
              variant="contained"
              disabled={!ready || phase !== 'idle'}
              onClick={() => {
                controls?.start();
                setPhase('inTake');
              }}
            >
              Start
            </Button>
            <Button
              variant="contained"
              disabled={!ready || phase !== 'inTake'}
              onClick={() => {
                controls?.submitTake();
                setPhase('canAdvance');
              }}
            >
              Submit
            </Button>
            <Button
              variant="contained"
              disabled={!ready || phase !== 'canAdvance'}
              onClick={() => {
                controls?.next();
                setPhase('inTake');
              }}
            >
              Next
            </Button>
            <Button
              variant="contained"
              disabled={!ready || phase === 'idle' || phase === 'done'}
              onClick={() => {
                controls?.end();
                setPhase('done');
              }}
            >
              End
            </Button>
          </Stack>
        );
      }}
    </ExercisePlayShell>
  );
};

export default ExercisePlay;
